// src/pages/PDFEditorPage.js
/**
 * PDF Editor v2 – Server-Rendered Architecture
 *
 * Pages rendered as PNG by PyMuPDF on backend, eliminating pdf.js font drift.
 * Text spans overlaid as interactive elements. Edits committed to actual PDF.
 */
import React, { useState, useCallback, useRef, useEffect, useReducer } from 'react';
import Toolbar from '../components/pdf-editor/Toolbar';
import SidePanel from '../components/pdf-editor/SidePanel';
import PDFViewer from '../components/pdf-editor/PDFViewer';
import SignatureModal from '../components/pdf-editor/SignatureModal';

const API_BASE = process.env.REACT_APP_PDF_API || '';

let _nextId = Date.now();
const uid = () => `el-${_nextId++}`;

/* ── Reducer ────────────────────────────────────────────────────────────── */
const initialState = {
  sessionId: null, fileName: null, fileSize: 0,
  pages: [], pageImages: {},
  elements: [], selectedId: null,
  deletedPdfElements: [],
  mode: 'select', zoom: 1.0,
  loading: false, saving: false, toast: null,
  signatureModalOpen: false, signatureDataUrl: null,
  undoStack: [], redoStack: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, loading: action.value };
    case 'SET_SAVING': return { ...state, saving: action.value };
    case 'SET_TOAST': return { ...state, toast: action.value };
    case 'SET_SESSION': {
      const { sessionId, fileName, fileSize, pages, elements } = action;
      return { ...state, sessionId, fileName, fileSize, pages, elements,
        loading: false, selectedId: null, undoStack: [], redoStack: [],
        deletedPdfElements: [], pageImages: {} };
    }
    case 'SET_PAGE_IMAGE':
      return { ...state, pageImages: { ...state.pageImages, [action.pageIndex]: action.url } };
    case 'SELECT': return { ...state, selectedId: action.id };
    case 'DESELECT': return { ...state, selectedId: null };
    case 'SET_MODE': return { ...state, mode: action.mode, selectedId: null };
    case 'SET_ZOOM': return { ...state, zoom: Math.max(0.3, Math.min(4, action.zoom)) };
    case 'UPDATE_ELEMENT':
      return { ...state, elements: state.elements.map(el => el.id === action.element.id ? action.element : el) };
    case 'ADD_ELEMENT':
      return { ...state, undoStack: [...state.undoStack, state.elements], redoStack: [],
        elements: [...state.elements, action.element], selectedId: action.element.id, mode: 'select' };
    case 'DELETE_ELEMENT': {
      const toDelete = state.elements.find(el => el.id === action.id);
      const newDeleted = (toDelete && toDelete.origin === 'pdf')
        ? [...state.deletedPdfElements, { pageIndex: toDelete.pageIndex, x: toDelete.origX ?? toDelete.x, y: toDelete.origY ?? toDelete.y, w: toDelete.origW ?? toDelete.w, h: toDelete.origH ?? toDelete.h, type: toDelete.type }]
        : state.deletedPdfElements;
      return { ...state, undoStack: [...state.undoStack, state.elements], redoStack: [],
        elements: state.elements.filter(el => el.id !== action.id),
        deletedPdfElements: newDeleted,
        selectedId: state.selectedId === action.id ? null : state.selectedId };
    }
    case 'PUSH_HISTORY':
      return { ...state, undoStack: [...state.undoStack, state.elements], redoStack: [] };
    case 'SET_ELEMENTS': return { ...state, elements: action.elements, deletedPdfElements: action.clearDeleted ? [] : state.deletedPdfElements };
    case 'UNDO': {
      if (!state.undoStack.length) return state;
      return { ...state, undoStack: state.undoStack.slice(0, -1),
        redoStack: [state.elements, ...state.redoStack],
        elements: state.undoStack[state.undoStack.length - 1], selectedId: null };
    }
    case 'REDO': {
      if (!state.redoStack.length) return state;
      return { ...state, undoStack: [...state.undoStack, state.elements],
        redoStack: state.redoStack.slice(1), elements: state.redoStack[0], selectedId: null };
    }
    case 'SET_SIGNATURE_MODAL': return { ...state, signatureModalOpen: action.open };
    case 'SET_SIGNATURE_DATA': return { ...state, signatureDataUrl: action.url };
    case 'MARK_COMMITTED':
      return { ...state, elements: state.elements.map(el =>
        el.origin === 'pdf' ? { ...el, committed: true, origText: el.text,
          origX: el.x, origY: el.y, origW: el.w, origH: el.h,
          origStyle: el.style ? { ...el.style } : {} } : el
      )};
    default: return state;
  }
}

/* ── DropZone ───────────────────────────────────────────────────────────── */
const DropZone = ({ onFile }) => {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  const processFile = f => {
    if (!f || f.type !== 'application/pdf') { alert('Only .pdf files are accepted'); return; }
    onFile(f);
  };
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <div onClick={() => ref.current?.click()}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => { e.preventDefault(); setOver(false); processFile(e.dataTransfer.files[0]); }}
        style={{ maxWidth: 480, width: '100%', padding: '56px 32px',
          border: `2px dashed ${over ? '#3b82f6' : '#cbd5e1'}`, borderRadius: 20,
          background: over ? '#eff6ff' : '#f8fafc', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 18, cursor: 'pointer', transition: 'all 0.2s',
          boxShadow: over ? '0 0 0 4px rgba(59,130,246,0.1)' : 'none' }}>
        <div style={{ width: 72, height: 72, borderRadius: 20,
          background: over ? 'rgba(59,130,246,0.1)' : 'rgba(148,163,184,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📄</div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>
            {over ? 'Drop your PDF here' : 'Upload a PDF to start editing'}</p>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            Drag & drop or <span style={{ color: '#3b82f6', fontWeight: 600 }}>click to browse</span></p>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>Max 50 MB</p>
        </div>
        <input ref={ref} type="file" accept=".pdf,application/pdf"
          style={{ display: 'none' }} onChange={e => processFile(e.target.files[0])} />
      </div>
    </div>
  );
};

/* ── Toast ───────────────────────────────────────────────────────────── */
const Toast = ({ message, type }) => (
  <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
    padding: '10px 22px', borderRadius: 9999, zIndex: 9999,
    background: type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#1e293b',
    color: '#fff', fontSize: 14, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
    animation: 'toastIn 0.25s ease', pointerEvents: 'none' }}>
    {message}
    <style>{`@keyframes toastIn {from{opacity:0;transform:translateX(-50%) translateY(10px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
  </div>
);

/* ── Loader ──────────────────────────────────────────────────────────── */
const Loader = ({ text }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 40 }}>
    <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid #e2e8f0',
      borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite' }} />
    <p style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{text || 'Loading…'}</p>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

/* ════════════════════════════════════════════════════════════════════════ */
/*  Main Component                                                         */
/* ════════════════════════════════════════════════════════════════════════ */
const PDFEditorPage = ({ setPage }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const imageInputRef = useRef(null);
  const pendingClickRef = useRef(null);
  const stateRef = useRef(state);
  const autoSaveTimerRef = useRef(null);
  const autoSaveQueuedRef = useRef(false);

  useEffect(() => {
    document.body.classList.add('native-cursor');
    return () => document.body.classList.remove('native-cursor');
  }, []);

  useEffect(() => { stateRef.current = state; }, [state]);

  const showToast = useCallback((msg, type = 'success') => {
    dispatch({ type: 'SET_TOAST', value: { message: msg, type } });
    setTimeout(() => dispatch({ type: 'SET_TOAST', value: null }), 3000);
  }, []);

  /* ── Fetch page image from backend ── */
  const fetchPageImage = useCallback(async (sessionId, pageIndex) => {
    try {
      const res = await fetch(`${API_BASE}/api/pdf/render-page/${sessionId}/${pageIndex}?scale=2`);
      if (!res.ok) throw new Error(`Render failed: ${res.status}`);
      const blob = await res.blob();
      dispatch({ type: 'SET_PAGE_IMAGE', pageIndex, url: URL.createObjectURL(blob) });
    } catch (err) { console.error(`Page ${pageIndex} render error:`, err); }
  }, []);

  /* ── Upload PDF ── */
  const handleFile = useCallback(async (file) => {
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/api/pdf/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Upload failed`);
      const data = await res.json();
      const elements = (data.elements || []).map(el => ({
        ...el, id: uid(), origin: 'pdf', committed: true,
        origText: el.text, origX: el.x, origY: el.y, origW: el.w, origH: el.h,
        origStyle: el.style ? { ...el.style } : {},
      }));
      dispatch({ type: 'SET_SESSION', sessionId: data.sessionId, fileName: data.fileName,
        fileSize: data.fileSize, pages: data.pages, elements });
      for (let i = 0; i < data.pages.length; i++) fetchPageImage(data.sessionId, i);
      if (elements.length > 0) showToast(`${elements.length} text elements loaded`);
    } catch (err) {
      dispatch({ type: 'SET_LOADING', value: false });
      showToast(err.message || 'Upload failed', 'error');
    }
  }, [fetchPageImage, showToast]);

  const handleUpdateElement = useCallback(updated => dispatch({ type: 'UPDATE_ELEMENT', element: updated }), []);
  const handleInteractionEnd = useCallback(() => dispatch({ type: 'PUSH_HISTORY' }), []);

  const handleCanvasClick = useCallback((pageIndex, pdfX, pdfY) => {
    const { mode, signatureDataUrl } = stateRef.current;
    if (mode === 'add-text') {
      dispatch({ type: 'ADD_ELEMENT', element: { id: uid(), type: 'text', pageIndex,
        x: pdfX, y: pdfY, w: 200, h: 24, text: 'New text', origin: 'user',
        style: { fontSize: 14, fontFamily: 'Helvetica, Arial, sans-serif',
          fontWeight: 'normal', fontStyle: 'normal', color: '#000000', textAlign: 'left' } } });
    } else if (mode === 'add-image') {
      pendingClickRef.current = { pageIndex, x: pdfX, y: pdfY };
      imageInputRef.current?.click();
      dispatch({ type: 'SET_MODE', mode: 'select' });
    } else if (mode === 'add-signature') {
      if (!signatureDataUrl) { dispatch({ type: 'SET_SIGNATURE_MODAL', open: true }); return; }
      dispatch({ type: 'ADD_ELEMENT', element: { id: uid(), type: 'image', kind: 'signature',
        pageIndex, x: pdfX, y: pdfY, w: 180, h: 70, src: signatureDataUrl, origin: 'user',
        style: { opacity: 1 } } });
    }
  }, []);

  const handleImageFile = useCallback(e => {
    const file = e.target.files[0]; if (!file) return;
    const pos = pendingClickRef.current || { pageIndex: 0, x: 50, y: 50 };
    const reader = new FileReader();
    reader.onload = ev => dispatch({ type: 'ADD_ELEMENT', element: { id: uid(), type: 'image',
      pageIndex: pos.pageIndex, x: pos.x, y: pos.y, w: 200, h: 150, src: ev.target.result,
      origin: 'user', style: { opacity: 1 } } });
    reader.readAsDataURL(file); e.target.value = '';
  }, []);

  const handleDelete = useCallback(() => {
    if (stateRef.current.selectedId) dispatch({ type: 'DELETE_ELEMENT', id: stateRef.current.selectedId });
  }, []);

  const handleSidePanelUpdate = useCallback(updated => {
    dispatch({ type: 'PUSH_HISTORY' });
    dispatch({ type: 'UPDATE_ELEMENT', element: { ...updated, committed: false } });
  }, []);

  const collectPendingChanges = useCallback((snapshot) => {
    const { elements, deletedPdfElements } = snapshot;
    const edits = elements.filter(el => el.type === 'text').filter(el => {
      if (el.origin === 'user') return true;
      if (el.origin === 'pdf' && !el.committed) return true;
      if (el.origin === 'pdf') {
        return el.text !== el.origText || el.x !== el.origX || el.y !== el.origY ||
          JSON.stringify(el.style) !== JSON.stringify(el.origStyle || {});
      }
      return false;
    }).map(el => ({ pageIndex: el.pageIndex, origText: el.origText, newText: el.text,
      oldX: el.origX, oldY: el.origY, oldW: el.origW, oldH: el.origH,
      x: el.x, y: el.y, w: el.w, h: el.h, style: el.style, pdfFont: el.pdfFont || '' }));

    const overlays = elements.filter(el => el.type === 'image' && el.src && el.origin === 'user')
      .map(el => ({ pageIndex: el.pageIndex, x: el.x, y: el.y, w: el.w, h: el.h, src: el.src,
        opacity: el.style?.opacity ?? 1 }));

    const deletions = deletedPdfElements || [];
    return { edits, overlays, deletions };
  }, []);

  /* ── Save ── */
  const handleSave = useCallback(async (opts = {}) => {
    const silent = Boolean(opts?.silent);
    const { sessionId } = stateRef.current;
    if (!sessionId) { showToast('No PDF loaded', 'error'); return; }
    const { edits, overlays, deletions } = collectPendingChanges(stateRef.current);
    if (!edits.length && !overlays.length && !deletions.length) {
      if (!silent) showToast('No changes to save');
      return;
    }

    if (stateRef.current.saving) {
      autoSaveQueuedRef.current = true;
      return;
    }

    dispatch({ type: 'SET_SAVING', value: true });
    try {
      const res = await fetch(`${API_BASE}/api/pdf/export`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, edits, overlays, deletions, saveBack: true }) });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      const resData = await res.json();
      // Replace ALL elements with fresh extraction from the saved PDF
      if (resData.elements) {
        const freshElements = resData.elements.map(el => ({
          ...el, id: uid(), origin: 'pdf', committed: true,
          origText: el.text, origX: el.x, origY: el.y, origW: el.w, origH: el.h,
          origStyle: el.style ? { ...el.style } : {},
        }));
        dispatch({ type: 'SET_ELEMENTS', elements: freshElements, clearDeleted: true });
      }
      // Re-render ALL pages (deletions can affect any page)
      const allPages = new Set([
        ...edits.map(e => e.pageIndex),
        ...overlays.map(o => o.pageIndex),
        ...deletions.map(d => d.pageIndex),
      ]);
      for (const pi of allPages) fetchPageImage(sessionId, pi);
      if (!silent) {
        showToast(`Saved ${edits.length + overlays.length + deletions.length} changes`);
      }
    } catch (err) {
      if (!silent) showToast(err.message, 'error');
    } finally {
      dispatch({ type: 'SET_SAVING', value: false });
      if (autoSaveQueuedRef.current) {
        autoSaveQueuedRef.current = false;
        setTimeout(() => handleSave({ silent: true }), 0);
      }
    }
  }, [showToast, fetchPageImage, collectPendingChanges]);

  /* ── Download ── */
  const handleDownload = useCallback(async () => {
    const { sessionId, elements, fileName, deletedPdfElements } = stateRef.current;
    if (!sessionId) { showToast('No PDF loaded', 'error'); return; }
    dispatch({ type: 'SET_SAVING', value: true });
    try {
      const { edits, overlays, deletions } = collectPendingChanges({ elements, deletedPdfElements });
      const res = await fetch(`${API_BASE}/api/pdf/export`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, edits, overlays, deletions, saveBack: false }) });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName?.replace('.pdf', '_edited.pdf') || 'edited.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('PDF downloaded');
    } catch (err) { showToast(err.message, 'error'); }
    finally { dispatch({ type: 'SET_SAVING', value: false }); }
  }, [showToast, collectPendingChanges]);

  useEffect(() => {
    if (!state.sessionId || state.loading) return;
    const { edits, overlays, deletions } = collectPendingChanges(state);
    if (!edits.length && !overlays.length && !deletions.length) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave({ silent: true });
    }, 1200);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [state, state.sessionId, state.loading, state.elements, state.deletedPdfElements, collectPendingChanges, handleSave]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const handleKey = e => {
      const mac = navigator.platform.toUpperCase().includes('MAC');
      const ctrl = mac ? e.metaKey : e.ctrlKey;
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (ctrl && e.key === 'z' && !e.shiftKey) { e.preventDefault(); dispatch({ type: 'UNDO' }); }
      if (ctrl && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) { e.preventDefault(); dispatch({ type: 'REDO' }); }
      if (ctrl && e.key === 's') { e.preventDefault(); handleSave(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && stateRef.current.selectedId) {
        e.preventDefault(); dispatch({ type: 'DELETE_ELEMENT', id: stateRef.current.selectedId }); }
      if (e.key === 'Escape') dispatch({ type: 'DESELECT' });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleSave]);

  const selectedElement = state.elements.find(el => el.id === state.selectedId) ?? null;
  const hasPdf = state.pages.length > 0;

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      position: 'relative',
      background: 'radial-gradient(1200px 600px at 15% -10%, rgba(14,165,233,0.16), transparent 55%), radial-gradient(900px 500px at 90% 10%, rgba(168,85,247,0.14), transparent 50%), #020617',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes pdfGridMove { from { transform: translateY(0); } to { transform: translateY(34px); } }
        @keyframes pdfGlowDrift { 0%,100% { transform: translateX(0) translateY(0); opacity: .28; } 50% { transform: translateX(16px) translateY(-12px); opacity: .5; } }
      `}</style>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(34,211,238,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.08) 1px, transparent 1px)',
        backgroundSize: '34px 34px',
        animation: 'pdfGridMove 8s linear infinite',
        maskImage: 'radial-gradient(circle at center, black 35%, transparent 100%)',
      }} />
      <div style={{
        position: 'absolute', width: 360, height: 360, borderRadius: '50%',
        top: -120, right: -120, background: 'radial-gradient(circle, rgba(14,165,233,0.25), transparent 68%)',
        filter: 'blur(8px)', animation: 'pdfGlowDrift 7s ease-in-out infinite', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', width: 320, height: 320, borderRadius: '50%',
        bottom: -120, left: -80, background: 'radial-gradient(circle, rgba(168,85,247,0.22), transparent 70%)',
        filter: 'blur(8px)', animation: 'pdfGlowDrift 9s ease-in-out infinite reverse', pointerEvents: 'none',
      }} />
      {/* Breadcrumb */}
      <div style={{ padding: '10px 20px', background: 'rgba(2,6,23,0.78)', borderBottom: '1px solid rgba(34,211,238,0.22)',
        backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8', position: 'relative', zIndex: 2 }}>
        <button onClick={() => setPage('/tools')} style={{ background: 'none', border: 'none',
          cursor: 'pointer', color: '#67e8f9', fontSize: 13, padding: 0 }}>Tools</button>
        <span style={{ color: '#cbd5e1' }}>›</span>
        <span style={{ fontWeight: 600, color: '#e2e8f0' }}>PDF Editor</span>
        {state.fileName && (<>
          <span style={{ color: '#cbd5e1' }}>›</span>
          <span style={{ color: '#cbd5e1' }}>{state.fileName}</span>
          <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px',
            borderRadius: 9999, background: 'rgba(16,185,129,0.16)', color: '#86efac', border: '1px solid rgba(134,239,172,0.45)' }}>
            {(state.fileSize / 1024).toFixed(0)} KB</span>
          {state.saving && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px',
            borderRadius: 9999, background: 'rgba(250,204,21,0.15)', color: '#fde68a', border: '1px solid rgba(253,224,71,0.45)' }}>Saving…</span>}
        </>)}
      </div>

      <Toolbar zoom={state.zoom}
        onZoomIn={() => dispatch({ type: 'SET_ZOOM', zoom: +(state.zoom + 0.15).toFixed(2) })}
        onZoomOut={() => dispatch({ type: 'SET_ZOOM', zoom: +(state.zoom - 0.15).toFixed(2) })}
        onZoomReset={() => dispatch({ type: 'SET_ZOOM', zoom: 1.0 })}
        canUndo={state.undoStack.length > 0} canRedo={state.redoStack.length > 0}
        onUndo={() => dispatch({ type: 'UNDO' })} onRedo={() => dispatch({ type: 'REDO' })}
        onSave={handleSave} onDownload={handleDownload}
        onAddText={() => dispatch({ type: 'SET_MODE', mode: 'add-text' })}
        onAddImage={() => dispatch({ type: 'SET_MODE', mode: 'add-image' })}
        onOpenSignature={() => dispatch({ type: 'SET_SIGNATURE_MODAL', open: true })}
        mode={state.mode} setMode={m => dispatch({ type: 'SET_MODE', mode: m })} hasPdf={hasPdf} />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0, position: 'relative', zIndex: 2 }}>
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto',
          cursor: state.mode !== 'select' ? 'crosshair' : 'default',
          background: 'linear-gradient(180deg, rgba(15,23,42,0.6), rgba(2,6,23,0.45))' }}>
          {state.loading && <Loader text={`Loading ${state.fileName || 'PDF'}…`} />}
          {!state.loading && !hasPdf && <DropZone onFile={handleFile} />}
          {!state.loading && hasPdf && (
            <PDFViewer pages={state.pages} pageImages={state.pageImages} zoom={state.zoom}
              elements={state.elements} selectedId={state.selectedId}
              onSelectElement={id => dispatch({ type: 'SELECT', id })}
              onDeselectAll={() => dispatch({ type: 'DESELECT' })}
              onUpdateElement={handleUpdateElement} onInteractionEnd={handleInteractionEnd}
              mode={state.mode} onCanvasClick={handleCanvasClick} />
          )}
        </div>
        <SidePanel selectedElement={selectedElement} onUpdateElement={handleSidePanelUpdate}
          onDeleteElement={handleDelete} />
      </div>

      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={handleImageFile} />
      <SignatureModal open={state.signatureModalOpen}
        onClose={() => dispatch({ type: 'SET_SIGNATURE_MODAL', open: false })}
        onSave={dataUrl => {
          dispatch({ type: 'SET_SIGNATURE_DATA', url: dataUrl });
          dispatch({ type: 'SET_MODE', mode: 'add-signature' });
          showToast('Click on the PDF to place your signature');
        }} />
      {state.toast && <Toast message={state.toast.message} type={state.toast.type} />}
    </div>
  );
};

export default PDFEditorPage;
